import type React from 'react';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  Mention,
  MentionsInput,
  type SuggestionDataItem,
  // biome-ignore lint/style/noRestrictedImports: legacy mentions input for old shares, removed in FE-8056
} from 'react-mentions';

import classNames from 'classnames';
import { debounce } from 'lodash-es';

import { getUserLabel } from '@iconik-io/core/src/get_user_label';
import {
  httpGetGroupsBasic,
  httpGetTeamsBasic,
  httpGetUsersBasic,
  type UsersGroupElasticSchemaWithIdNullable,
  type UsersUserBasicElasticSchemaWithIdNullable,
} from '@iconik-io/iconik-api/src/services/users';
import { FLAG_SIMPLIFIED_ACLS } from '@iconik-io/shared-lib/src/analytics/flags';
import { growthbook } from '@iconik-io/shared-lib/src/analytics/growthbook';
import {
  type AriaProps,
  ariaToReactProps,
} from '@iconik-io/shared-lib/src/react/a11y/aria';

import Ellipsis from '../../components/ellipsis/ellipsis';
import UserBubble from '../../components/user_bubble/user_bubble';
import { INPUT_DEBOUNCE_TIMEOUT } from '../../constants/debounce';
import { email } from '../../constants/regex';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import { targetElementOf } from '../../utils/event_target';
import { fullscreenElementOrBody } from '../../utils/fullscreen_element_or_body';
import type { FormFieldEvent } from '../forms/types';
import { markup } from './utils';

import styles from './mention_input.module.css';

const markupRegex = /@(.+?)\[id:([^\]]+?)\]/;
const mentionRegex = /\[id:([^\]]+?)\]/g;

const emptyGroupsResponse = {
  body: {
    objects: [],
  },
};

const getPastedOptions = (textValue: string, regex: RegExp, type: string) => {
  const matches = textValue.match(regex) || [];

  const results = matches.flatMap((item) => {
    const values = item.split('[');
    if (values.length === 2) {
      const id = values[1].replace(`${type}:`, '').replace(']', '');
      const display = values[0].replace('@', '').replace(']', '');
      return [{ id, display, type }];
    }
    return [];
  });

  return results;
};

type PastedOption = { display: string; id: string; type: string };

type SuggestionLabelProps = { label: string };

const SuggestionLabel: FC<SuggestionLabelProps> = ({ label }) => (
  <div
    className={styles.mentions__suggestion_label}
    onMouseDown={(event) => event.stopPropagation()}
  >
    {label}
  </div>
);

type SuggestionOption =
  | UsersUserBasicElasticSchemaWithIdNullable
  | UsersGroupElasticSchemaWithIdNullable;

// The list mixes users and groups and tells them apart by position, so a photo
// is read only from the entries that carry one.
const suggestionPhoto = (option: SuggestionOption | undefined) =>
  option && 'photo_small' in option
    ? (option.photo_small ?? undefined)
    : undefined;

const UserSuggestion: FC<{
  suggestion: SuggestionDataItem;
  photoUrl?: string;
}> = ({ suggestion, photoUrl }) => (
  <div className={styles.mentions__suggestion}>
    <div className={styles.avatar}>
      <UserBubble
        src={photoUrl ?? ''}
        size="sm"
        name={suggestion.display || ''}
      />
    </div>
    <Ellipsis componentProps={{ className: styles.name }}>
      {suggestion.display}
    </Ellipsis>
  </div>
);

// Groups come back from search without any avatar, so the bubble always
// renders initials.
const GroupSuggestion: React.FC<{
  suggestion: SuggestionDataItem;
}> = ({ suggestion }) => (
  <div className={styles.mentions__suggestion}>
    <div className={styles.avatar}>
      <UserBubble src="" size="sm" name={suggestion.display || ''} />
    </div>
    <Ellipsis componentProps={{ className: styles.name }}>
      {suggestion.display}
    </Ellipsis>
  </div>
);

export type MentionsMode =
  | 'usersAndEmails'
  | 'usersAndGroups'
  | 'usersAndTeams';

export type MentionInputProps = {
  id?: string;
  name?: string;
  value?: string;
  className?: string;
  error?: boolean;
  onClick?: () => void;
  onKeyDown?: (
    event:
      | React.KeyboardEvent<HTMLTextAreaElement>
      | React.KeyboardEvent<HTMLInputElement>,
  ) => void;
  onChange?: (value: string, event?: FormFieldEvent | undefined) => void;
  onBlur?: (value: string, event: FormFieldEvent) => void;
  placeholderPrefix?: string;
  disabled?: boolean;
  mentionsMode?: MentionsMode;
  allowSearchUsers?: boolean;
  aria?: AriaProps;
  autoFocus?: boolean;
};

export type ValueType = string;

const parseValue = (value: string, mentionsMode: MentionsMode) =>
  mentionsMode === 'usersAndEmails'
    ? value.replace(/\[email:/g, '[id:').replace(/\[user_id:/g, '[id:')
    : value.replace(/\[group_id:/g, '[id:').replace(/\[user_id:/g, '[id:');

const getPlaceholderText = (mentionsMode: MentionsMode) => {
  switch (mentionsMode) {
    case 'usersAndEmails':
      return 'Type @ and email to mention a user (example: `@user@example.com`)';
    case 'usersAndGroups':
      return 'Type @ to mention a group or user...';
    case 'usersAndTeams':
      return 'Type @ to mention a team or user...';
  }
};

const MentionInput: FC<MentionInputProps> = (props) => {
  const {
    error,
    onClick,
    onKeyDown,
    onChange,
    value = '',
    name,
    mentionsMode = growthbook?.isOn(FLAG_SIMPLIFIED_ACLS)
      ? 'usersAndTeams'
      : 'usersAndGroups',
    allowSearchUsers = true,
    disabled,
    placeholderPrefix,
    autoFocus,
  } = props;

  const dispatch = useAppDispatch();

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [options, setOptions] = useState<SuggestionOption[]>([]);
  const usersData = useAppSelector((state) => state.entities.users_basic);
  const existingUserOptions = Object.entries(usersData).map(([id, user]) => ({
    id,
    display: getUserLabel(user),
  }));

  const [pastedOptions, setPastedOptions] = useState<PastedOption[]>([]);

  const [firstGroupIndex, setFirstGroupIndex] = useState<number | null>(null);

  const getGroupOrTeamOptions = (query: string) => {
    switch (mentionsMode) {
      case 'usersAndGroups':
        return dispatch(
          httpGetGroupsBasic({ query: `${query}*`, per_page: 100 }),
        );
      case 'usersAndTeams':
        return dispatch(
          httpGetTeamsBasic({ query: `${query}*`, per_page: 100 }),
        );
      default:
        return Promise.resolve(emptyGroupsResponse);
    }
  };

  const getOptions = useCallback(
    debounce(
      (query, callback) =>
        Promise.allSettled([
          dispatch(
            httpGetUsersBasic({
              query: `${query}*`,
              sort: 'first_name,asc',
              per_page: 100,
            }),
          ),
          getGroupOrTeamOptions(query),
        ]).then((results) => {
          const [usersResult, groupsResult] = results;
          const users =
            usersResult.status === 'fulfilled'
              ? usersResult.value.body.objects
              : [];
          const groups =
            groupsResult.status === 'fulfilled'
              ? groupsResult.value.body.objects
              : [];

          const values = [...users, ...groups];
          const userValues = users.map((item) => ({
            id: item.id,
            display: getUserLabel(item),
          }));
          const groupValues = groups.map((item) => ({
            id: item.id,
            display: item.name,
          }));

          setFirstGroupIndex(users.length);
          setOptions(values);

          return callback([...userValues, ...groupValues]);
        }),
      INPUT_DEBOUNCE_TIMEOUT,
    ),
    [],
  );

  const handleChange = (eventValue: string) => {
    if (!onChange) {
      return;
    }

    if (mentionsMode === 'usersAndGroups' || mentionsMode === 'usersAndTeams') {
      const newValue = eventValue.replace(mentionRegex, (item) => {
        const id = item.slice(
          item.lastIndexOf('[id:') + 4,
          item.lastIndexOf(']'),
        );

        const isAddedUser = value.includes(`user_id:${id}`);

        if (isAddedUser) {
          return `[user_id:${id}]`;
        }

        const isAddedGroup = value.includes(`group_id:${id}`);

        if (isAddedGroup) {
          return `[group_id:${id}]`;
        }

        const optionIndex = options.findIndex(
          (optionValue) => optionValue.id === id,
        );

        if (optionIndex >= 0) {
          const isUser =
            firstGroupIndex !== null
              ? optionIndex < firstGroupIndex && firstGroupIndex !== 0
              : true;

          return `[${isUser ? 'user_id:' : 'group_id:'}${id}]`;
        }

        const pastedOptionIndex = pastedOptions.findIndex(
          (optionValue) => optionValue.id === id,
        );

        if (pastedOptionIndex >= 0) {
          return `[${pastedOptions[pastedOptionIndex].type}:${id}]`;
        }

        return item;
      });

      onChange(newValue);
    } else {
      const newUsersValue = eventValue.replace(mentionRegex, (item) => {
        const id = item.slice(
          item.lastIndexOf('[id:') + 4,
          item.lastIndexOf(']'),
        );

        if (usersData[id]) {
          return `[user_id:${id}]`;
        }

        return item;
      });

      const newValue = newUsersValue.replace(/@(.+?)\s/g, (item) => {
        const emailValue = item.slice(1, item.indexOf('[') || item.length);
        const isEmail = email.test(emailValue);

        if (isEmail) {
          return `@${emailValue}[email:${emailValue}] `;
        }

        return item;
      });

      onChange(newValue);
    }
  };

  useEffect(() => {
    const onCopyCut = (event: ClipboardEvent): void => {
      const target = targetElementOf(event.target, HTMLTextAreaElement);
      const isTarget = target?.name === name;

      if (isTarget) {
        event.preventDefault();
        event.clipboardData?.setData('text/react-mentions', value || '');
      }
    };
    const onPaste = (event: ClipboardEvent): void => {
      const target = targetElementOf(event.target, HTMLTextAreaElement);
      const isTarget = target?.name === name;

      if (isTarget) {
        const textValue =
          event.clipboardData?.getData('text/react-mentions') || '';

        const resultsUsers = getPastedOptions(
          textValue,
          /@.+?\[user_id:.+?]/g,
          'user_id',
        );
        const resultsGroups = getPastedOptions(
          textValue,
          /@.+?\[group_id:.+?]/g,
          'group_id',
        );

        setPastedOptions([...resultsUsers, ...resultsGroups]);
      }
    };

    document.addEventListener('paste', onPaste);
    document.addEventListener('copy', onCopyCut);
    document.addEventListener('cut', onCopyCut);
    return () => {
      document.removeEventListener('paste', onPaste);
      document.removeEventListener('cut', onCopyCut);
      document.removeEventListener('copy', onCopyCut);
    };
  }, [value, name]);

  const placeholder = [placeholderPrefix, getPlaceholderText(mentionsMode)]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classNames({ [styles.mentions_error]: error })}>
      <MentionsInput
        disabled={disabled}
        value={parseValue(value, mentionsMode)}
        onKeyDown={onKeyDown}
        inputRef={inputRef}
        allowSpaceInQuery
        name={name}
        onClick={onClick}
        onTouchStart={onClick}
        forceSuggestionsAboveCursor
        allowSuggestionsAboveCursor
        suggestionsPortalHost={fullscreenElementOrBody()}
        placeholder={placeholder}
        className="mentions"
        onBlur={(event) => props.onBlur?.(event.target.value, event)}
        classNames={styles}
        onChange={(event) => {
          handleChange(event.target.value);
        }}
        autoFocus={autoFocus}
        {...ariaToReactProps(props.aria)}
      >
        <Mention
          trigger="@"
          markup={markup}
          regex={markupRegex}
          data={allowSearchUsers ? getOptions : existingUserOptions}
          appendSpaceOnAdd
          renderSuggestion={(
            suggestion,
            _search,
            _highlightedDisplay,
            index,
          ) => {
            const isUser =
              firstGroupIndex !== null
                ? index < firstGroupIndex && firstGroupIndex !== 0
                : true;
            return isUser ? (
              <>
                {index === 0 ? <SuggestionLabel label="Users" /> : null}
                <UserSuggestion
                  suggestion={suggestion}
                  photoUrl={suggestionPhoto(options[index])}
                />
              </>
            ) : (
              <>
                {index === firstGroupIndex ? (
                  <SuggestionLabel
                    label={
                      growthbook?.isOn(FLAG_SIMPLIFIED_ACLS)
                        ? 'Teams'
                        : 'Groups'
                    }
                  />
                ) : null}
                <GroupSuggestion suggestion={suggestion} />
              </>
            );
          }}
          className={styles.mentions__mention}
        />
      </MentionsInput>
    </div>
  );
};

export default MentionInput;
